// src/pages/Login.js
import { logger } from "../utils/logger";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { normalizeRole, ROLE_VALUE } from "../constants/roleLabels";
import { BRAND } from "../config/brand";
import BrandMark from "../components/BrandMark";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Brain,
  Shield,
  Zap,
  ArrowRight,
  Check,
} from "lucide-react";

export default function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUserRole } = useAuth();
  
  // Get return URL from location state (passed by PrivateRoute)
  const from = location.state?.from?.pathname || null;

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      logger.log("🔑 Attempting login:", formData.email);

      // 1️⃣ Login with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      await userCredential.user.reload();
      const user = auth.currentUser;
      logger.log("✅ Firebase user:", user);

    
      // 3️⃣ Fetch Firestore user document for role + name
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      let data = userDoc.data();

      // 4️⃣ Ensure Firestore profile exists even if user verified elsewhere
      if (!userDoc.exists()) {
        const fallbackRole =
          normalizeRole(localStorage.getItem("userRole")) ||
          ROLE_VALUE.requester;
        const fallbackProfile = {
          uid: user.uid,
          email: user.email || formData.email,
          name: user.displayName || user.email?.split("@")[0] || "User",
          role: fallbackRole,
        };

        await setDoc(
          userDocRef,
          {
            ...fallbackProfile,
            emailVerified: true,
            createdAt: serverTimestamp(),
            verifiedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        data = fallbackProfile;
      }

      let role = ROLE_VALUE.requester; // default fallback
      let name = user.displayName || "";
      if (data.role) role = normalizeRole(data.role) || ROLE_VALUE.requester;
      if (data.name) name = data.name;
      const emailVerified = user.emailVerified || Boolean(data.emailVerified);

      // 5️⃣ Update Auth Context with role
      setCurrentUserRole(role);
      localStorage.setItem("userRole", role);

      // 6️⃣ Store full user info in localStorage
      const currentUserData = {
        uid: user.uid,
        email: user.email,
        name: name,
        role: role,
        emailVerified,
      };
      localStorage.setItem("currentUser", JSON.stringify(currentUserData));

      logger.log("💾 Stored in localStorage:", currentUserData);

      // 7️⃣ Success toast + redirect
      toast.success("Logged in successfully.");
      setFormData({ email: "", password: "" });

      // Redirect to return URL if provided (e.g., from email link), otherwise go to dashboard
      if (from) {
        navigate(from, { replace: true });
      } else {
        navigate(
          role === ROLE_VALUE.specialist
            ? "/dashboard-helper"
            : "/dashboard-client"
        );
      }
    } catch (error) {
      console.error("❌ Login error:", error);

      // Map Firebase errors to friendly messages
      let message = "Login failed. Please try again.";
      if (error.code === "auth/user-not-found") {
        message = "No account found with this email.";
      } else if (error.code === "auth/wrong-password") {
        message = "Incorrect password. Please try again.";
      } else if (error.code === "auth/invalid-email") {
        message = "Please enter a valid email address.";
      } else if (error.code === "auth/too-many-requests") {
        message =
          "Too many login attempts. Please wait a moment and try again.";
      }

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Side - Product preview */}
          <div className="hidden lg:block">
            <div className="relative bg-slate-900 rounded-2xl p-8 text-white">
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-slate-700">
                  <div className="flex items-center gap-3">
                    <BrandMark size={40} boxed />
                    <div>
                      <h3 className="font-bold text-white">{BRAND.name}</h3>
                      <p className="text-xs text-slate-400">Intelligence Workspace</p>
                    </div>
                  </div>
                  <Brain className="w-5 h-5 text-blue-400" />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                    <span>Intelligence matching active</span>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-300">Problem analysis</span>
                      <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-300">Expert matching</span>
                      <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-300">Connection</span>
                      <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: '70%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Card */}
          <div className="w-full">
            <Link to="/" className="mb-6 flex items-center justify-center gap-2 lg:justify-start">
              <BrandMark size={32} />
              <span className="text-base font-semibold text-slate-900">{BRAND.name}</span>
            </Link>

            <div className="bg-white rounded-2xl shadow-sm p-8 md:p-10 max-w-md mx-auto border border-slate-200">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                  Welcome back
                </h1>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Continue where you left off solving real problems with real experts.
                </p>
              </div>

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Forgot Password */}
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium transition"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Logging in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Sign Up Link */}
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Don't have an account?{" "}
                  <Link
                    to="/signup"
                    className="text-blue-600 hover:text-blue-700 font-semibold transition"
                  >
                    Sign up now
                  </Link>
                </p>
              </div>

              {/* Trust Signals */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="flex flex-col items-center">
                    <Shield className="w-5 h-5 text-emerald-500 mb-1" />
                    <span className="text-xs text-slate-600">Secure</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <Zap className="w-5 h-5 text-blue-500 mb-1" />
                    <span className="text-xs text-slate-600">Fast</span>
                  </div>
                </div>
              </div>

            </div>

            {/* Trust Details */}
            <div className="mt-4 max-w-md mx-auto space-y-2 text-center">
              <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-emerald-500" />
                  <span>Secure login — powered by Firebase Auth</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-emerald-500" />
                  <span>Your data is encrypted and private</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
